/**
 * Item Variant Model
 *
 * Represents a single variant of an item with multi-dimensional options.
 * Used for items with flavor, size, color, or other variations where each
 * combination has unique pricing, SKU, and UPC.
 *
 * Example: Arizona Iced Tea with Flavor (Lemon, Peach) × Size (16oz, 20oz)
 * results in 4 variants, each with its own UPC and price.
 */

export interface IVariantOption {
  readonly name: string;        // Dimension name: "Flavor", "Size", "Color", etc.
  readonly value: string;       // Option value: "Lemon", "16oz", "Red", etc.
}

export interface IItemVariant {
  readonly id?: number;
  readonly guid?: string;
  readonly number?: number;
  readonly options: IVariantOption[];      // Combination of dimension values
  readonly displayName?: string;           // Auto-generated or custom: "Lemon 16oz"
  readonly price: number;                  // Variant-specific price
  readonly sku?: string;                   // Variant-specific SKU
  readonly upc?: string;                   // Variant-specific UPC (for barcode scanning)
  readonly barcode?: string;               // Alternative barcode format
  readonly available: boolean;             // Stock availability
  readonly calories?: number;              // Variant-specific calorie count
  readonly imageUrl?: string;              // Variant-specific image
  readonly position?: number;              // Display order
}

export interface IItemVariantData {
  id?: number;
  guid?: string;
  number?: number;
  options: IVariantOption[];
  displayName?: string;
  price: number;
  sku?: string;
  upc?: string;
  barcode?: string;
  available?: boolean;
  calories?: number;
  imageUrl?: string;
  position?: number;
}

export class ItemVariant implements IItemVariant {
  readonly id?: number;
  readonly guid?: string;
  readonly number?: number;
  readonly options: IVariantOption[];
  readonly displayName?: string;
  readonly price: number;
  readonly sku?: string;
  readonly upc?: string;
  readonly barcode?: string;
  readonly available: boolean;
  readonly calories?: number;
  readonly imageUrl?: string;
  readonly position?: number;

  constructor(data: IItemVariantData) {
    this.id = data.id;
    this.guid = data.guid;
    this.number = data.number;

    // Validate options
    if (!data.options || data.options.length === 0) {
      throw new Error('ItemVariant.constructor: options array is required and cannot be empty');
    }

    this.options = data.options;
    this.displayName = data.displayName || this.generateDisplayName();
    this.price = data.price;
    this.sku = data.sku;
    this.upc = data.upc;
    this.barcode = data.barcode;
    this.available = data.available ?? true;
    this.calories = data.calories;
    this.imageUrl = data.imageUrl;
    this.position = data.position;
  }

  /**
   * Auto-generate display name from option values
   * Example: [{ name: "Flavor", value: "Lemon" }, { name: "Size", value: "16oz" }]
   *          → "Lemon 16oz"
   */
  private generateDisplayName(): string {
    return this.options.map(opt => opt.value).join(' ');
  }

  /**
   * Get the value of a specific option by name
   * @param optionName - The dimension name (e.g., "Flavor", "Size")
   * @returns The option value or undefined if not found
   */
  getOption(optionName: string): string | undefined {
    return this.options.find(opt => opt.name === optionName)?.value;
  }

  /**
   * Check if this variant matches a set of option criteria
   * @param searchOptions - Object with dimension names as keys and values to match
   * @returns true if all search options match this variant's options
   *
   * Example:
   *   variant.matchesOptions({ Flavor: 'Lemon', Size: '16oz' })
   */
  matchesOptions(searchOptions: Record<string, string>): boolean {
    return Object.entries(searchOptions).every(([name, value]) =>
      this.getOption(name) === value
    );
  }

  /**
   * Check if this variant matches by any code (SKU, UPC, or barcode)
   * @param code - The code to search for
   * @returns true if code matches any of sku, upc, or barcode
   */
  matchesCode(code: string): boolean {
    if (!code) return false;
    return this.sku === code || this.upc === code || this.barcode === code;
  }

  toJSON(): IItemVariant {
    return {
      id: this.id,
      guid: this.guid,
      number: this.number,
      options: this.options,
      displayName: this.displayName,
      price: this.price,
      sku: this.sku,
      upc: this.upc,
      barcode: this.barcode,
      available: this.available,
      calories: this.calories,
      imageUrl: this.imageUrl,
      position: this.position,
    };
  }

  static fromJSON(data: Record<string, unknown>): ItemVariant {
    if (!data.options || !Array.isArray(data.options)) {
      throw new Error('ItemVariant.fromJSON: options is required and must be an array');
    }
    if (data.price === undefined || data.price === null) {
      throw new Error('ItemVariant.fromJSON: price is required');
    }

    return new ItemVariant({
      id: data.id as number | undefined,
      guid: data.guid as string | undefined,
      number: data.number as number | undefined,
      options: data.options as IVariantOption[],
      displayName: data.displayName as string | undefined,
      price: data.price as number,
      sku: data.sku as string | undefined,
      upc: data.upc as string | undefined,
      barcode: data.barcode as string | undefined,
      available: data.available as boolean | undefined,
      calories: data.calories as number | undefined,
      imageUrl: data.imageUrl as string | undefined,
      position: data.position as number | undefined,
    });
  }
}
